import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
  onIdTokenChanged,
  signInWithEmailAndPassword,
  signOut as fbSignOut,
} from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '@/lib/firebase';
import { guardarUltimoUid } from '@/lib/ultimoUid';
import { limparEstadoDeSessao } from '@/lib/estadoDeSessao';
import { definirContextoDeErro, tratarErro } from '@/lib/erros';

const AuthContext = createContext(null);

/**
 * Sessão do usuário: identidade (Auth) + claims de tenancy (clinicaId, papel)
 * + o documento do usuário dentro da clínica.
 *
 * clinicaId e papel vêm de custom claims, que só as Cloud Functions escrevem —
 * o cliente nunca consegue forjar o próprio tenant.
 */
export function AuthProvider({ children }) {
  const [carregando, setCarregando] = useState(true);
  const [usuario, setUsuario] = useState(null);
  const [claims, setClaims] = useState(null);
  const [perfil, setPerfil] = useState(null);
  /** ★ WT: a tela precisa saber que o PERFIL não carregou — senão a pessoa aparece sem função. */
  const [avisoPerfil, setAvisoPerfil] = useState(false);

  useEffect(() => {
    // Quem estava logado no quadro anterior. O estado preservado em memória é
    // por PESSOA: sessão que expira ou troca de conta não passa pelo botão
    // "Sair", e sem esta guarda a lista de pacientes da recepcionista anterior
    // continuaria em memória para quem entrasse depois.
    let uidAnterior = auth.currentUser?.uid ?? null;

    return onIdTokenChanged(auth, async (u) => {
      const uid = u?.uid ?? null;
      // ★★ WU/U3: a inicialização precisa do último uid para renovar a cópia local certa.
      guardarUltimoUid(uid);
      if (uid !== uidAnterior) {
        limparEstadoDeSessao();
        uidAnterior = uid;
      }

      if (!u) {
        setUsuario(null);
        setClaims(null);
        setPerfil(null);
        setCarregando(false);
        return;
      }
      const token = await u.getIdTokenResult();
      setUsuario(u);
      setClaims(token.claims);
      setCarregando(false);
    });
  }, []);

  // Perfil dentro da clínica (nome, ramal, metas). Superadmin pode não ter.
  /**
   * ★★★ WT lote 1 — QUEM É A PESSOA, para o registro de erro.
   *
   * ⚠ `window.onerror` e `unhandledrejection` disparam FORA da árvore do React: eles não
   * têm acesso a contexto nenhum, e eram justamente os erros sem dono. ★ Por isso a sessão
   * é espelhada num módulo (`lib/erros.js`), atualizada aqui a cada troca de token.
   */
  useEffect(() => {
    definirContextoDeErro({
      clinicaId: claims?.clinicaId ?? null,
      uid: usuario?.uid ?? null,
      papel: claims?.papel ?? null,
    });
  }, [usuario?.uid, claims?.clinicaId, claims?.papel]);

  useEffect(() => {
    const clinicaId = claims?.clinicaId;
    if (!usuario || !clinicaId) {
      setPerfil(null);
      return;
    }
    setAvisoPerfil(false);
    const ref = doc(db, 'clinicas', clinicaId, 'usuarios', usuario.uid);
    return onSnapshot(
      ref,
      (snap) => setPerfil(snap.exists() ? { id: snap.id, ...snap.data() } : null),
      // ★ WT: era SÓ console (medição §1.2) — nome e função do próprio usuário sumiam calados
      tratarErro('perfil do usuário', () => setAvisoPerfil(true)),
    );
  }, [usuario, claims?.clinicaId]);

  const valor = useMemo(() => {
    const papel = claims?.papel ?? null;
    return {
      carregando,
      usuario,
      claims,
      perfil,
      // ★ WT: `true` quando a leitura do perfil FALHOU (diferente de perfil inexistente).
      avisoPerfil,
      papel,
      clinicaId: claims?.clinicaId ?? null,
      autenticado: Boolean(usuario),
      // Sem claims o usuário existe no Auth mas ainda não foi vinculado a
      // nenhuma clínica — estado real durante o onboarding.
      semVinculo: Boolean(usuario) && !claims?.clinicaId && papel !== 'superadmin',
      isSuperadmin: papel === 'superadmin',
      isGestor: papel === 'gestor',
      temPapel: (...papeis) => papeis.includes(papel),
      entrar: (email, senha) => signInWithEmailAndPassword(auth, email, senha),
      // Limpa o estado preservado ANTES de sair. O computador da recepção é
      // compartilhado: sem isto, quem entrasse depois abriria "Confirmar
      // agenda" e encontraria a lista de pacientes de quem usou antes — nome e
      // telefone de todo mundo, sem ter carregado PDF nenhum.
      sair: () => {
        limparEstadoDeSessao();
        return fbSignOut(auth);
      },
      // Recarrega o token depois que uma Function altera claims.
      recarregarClaims: async () => {
        if (!auth.currentUser) return;
        await auth.currentUser.getIdToken(true);
        const token = await auth.currentUser.getIdTokenResult();
        setClaims(token.claims);
      },
    };
  }, [carregando, usuario, claims, perfil]);

  return <AuthContext.Provider value={valor}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth precisa estar dentro de <AuthProvider>.');
  return ctx;
}
